import React, { useCallback, useMemo, useRef } from "react";
import Scrollbars from "react-custom-scrollbars-2";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Button, EmptySlate, useResizeObserver } from "@activecollab/components";
import moment from "moment";
import PropTypes from "prop-types";
import { CalendarTasks } from "~/components/Calendar/Agenda/CalendarTasks";
import { useCreateTaskContext } from "~/components/Providers/CreateTask/context";
import { getCalendarCountersByDate } from "~/store/selectors/selector.calendars";
import { getHasActiveProjects } from "~/store/selectors/selector.projects";
import { hasLoggedUserElevatedPermissions } from "~/store/selectors/selector.users";
import Angie from "~/utils/angie";
import { CalendarCreateTask } from "./CalendarCreateTask";
import { CalendarReminders } from "./CalendarReminders";
import { CalendarTasksLoader } from "./CalendarTasksLoader";
import { CalendarUnavailablePeople } from "./CalendarUnavailablePeople";
import { useCalendarContext } from "../CalendarContext";

export const AGENDA = "agenda";
export const Agenda = ({ loading, isLoggedUserClient }) => {
  const { t } = useTranslation();
  const { selectedDate } = useCalendarContext();
  const canCreateTask = useSelector(hasLoggedUserElevatedPermissions);
  const { openModal, setModalOpenFrom } = useCreateTaskContext();
  const hasActiveProjects = useSelector(getHasActiveProjects);

  const counters = useSelector((state) => getCalendarCountersByDate(state, selectedDate));

  const showEmptySlate = useMemo(() => {
    if (isLoggedUserClient) {
      if (counters.tasks > 0 || counters.subtasks > 0 || counters.ghostTasks > 0) {
        return false;
      }
      return true;
    }
    if (
      counters.tasks > 0 ||
      counters.subtasks > 0 ||
      counters.ghostTasks > 0 ||
      counters.users > 0 ||
      counters.reminders > 0
    ) {
      return false;
    }
    return true;
  }, [counters, isLoggedUserClient]);

  const ref = useRef(document.body);
  const dimensions = useResizeObserver(ref);

  const onOpenModal = useCallback(() => {
    setModalOpenFrom(AGENDA);
    openModal({ presetNoAssignee: true, assigneeId: 0, dueOn: moment.utc(selectedDate).unix() });
  }, [openModal, selectedDate, setModalOpenFrom]);

  if (loading) {
    return <CalendarTasksLoader />;
  }

  return (
    <Scrollbars
      autoHeightMax="none"
      autoHeight={dimensions?.width <= 1024}
    >
      {showEmptySlate ? (
        <div className="tw-flex tw-flex-1 tw-items-center tw-justify-center tw-h-full tw-py-6">
          <EmptySlate
            src={`${Angie.assets_url}/system/images/layout/my_tasks_empty_page.png`}
            alt="No tasks"
            title={t("No Tasks")}
            description={`${t(
              "This list will show unavailable people, tasks, subtasks or recurring tasks scheduled on this day"
            )}.`}
          >
            {hasActiveProjects ? (
              <EmptySlate.Footer>
                {canCreateTask && (
                  <Button
                    variant="secondary"
                    onClick={onOpenModal}
                  >
                    {t("Add Task")}
                  </Button>
                )}
              </EmptySlate.Footer>
            ) : null}
          </EmptySlate>
        </div>
      ) : (
        <div className="tw-p-6 tw-flex tw-flex-col tw-gap-6">
          <CalendarTasks>
            {hasActiveProjects ? (
              <CalendarCreateTask
                canCreate={canCreateTask}
                onOpenModal={onOpenModal}
              />
            ) : null}
          </CalendarTasks>

          {!isLoggedUserClient ? (
            <>
              <CalendarReminders date={selectedDate} />
              <CalendarUnavailablePeople date={selectedDate} />
            </>
          ) : null}
        </div>
      )}
    </Scrollbars>
  );
};

Agenda.propTypes = {
  loading: PropTypes.bool,
  isLoggedUserClient: PropTypes.bool,
};
